// PX-Connect-Erweiterung des Korrespondenz-Modals (UCPWO-387 / ADR 0004, 0005).
//
// Stand UCPWO-392: Typ („PX Connect Nachricht", kuerzel 'PXC') UND Subtypen sind inzwischen
// echte DB-Snippets — die Subtypen liegen unter der eigenständigen eigenschaft „PXSubtyp" und
// werden im PXC-Kontext datengetrieben geladen (kein Mock mehr). Erkennung/Sichtbarkeit laufen
// über Kürzel bzw. `sourceSystem`. Was hier bleibt: die reine Display-Logik des
// Terminerfassungs-Blocks (Feldkatalog + Reihenfolge-Regeln), ausgelöst über den Subtyp
// „Terminerfassung"; persistiert wird nach `Projekt.metadata` (ADR 0005), NICHT als Korrespondenz.

/** Kürzel des echten PX-Connect-Korrespondenztyp-Snippets (name „PX Connect Nachricht"). */
export const PXC_TYPE_KUERZEL = 'PXC';

/** Kürzel des PX-Subtyps (eigenschaft „PXSubtyp", name „Terminerfassung"), der den Terminerfassungs-Block auslöst. */
export const PXC_SUBTYP_TERMINERFASSUNG_KUERZEL = 'TE';

/** Kürzel des PX-Subtyps (eigenschaft „PXSubtyp", name „Schadenbericht") für die aus dem Schadenbericht vorbereitete Korrespondenz. */
export const PXC_SUBTYP_SCHADENBERICHT_KUERZEL = 'SB';

/**
 * Kürzel der PX-Subtypen, die nur das System vergibt (Nachtrag 'NT', Stornierung 'ST' — Migration
 * 20261001120000): ucpw-services legt damit die Korrespondenz beim Nachtrag bzw. Storno an. Von Hand
 * sind sie nicht wählbar (UCPWO-398 Punkt 13), genau wie bei GDV.
 */
export const PXC_SUBTYP_NUR_SYSTEM_KUERZEL = ['NT', 'ST'];

type SnippetOption = { value?: any; label?: string; key?: string } | null | undefined;

/** PX-Connect-Korrespondenztyp (echtes Snippet) — erkannt am stabilen Kürzel 'PXC'. */
export const isPxConnectType = (option?: SnippetOption): boolean =>
    option?.key === PXC_TYPE_KUERZEL;

/** Trigger für den Terminerfassungs-Block: Typ „PX Connect Nachricht" (PXC) + Subtyp „Terminerfassung" (TE). */
export const isPxConnectTerminmitteilung = (typeOption?: SnippetOption, subtypOption?: SnippetOption): boolean =>
    isPxConnectType(typeOption) && subtypOption?.key === PXC_SUBTYP_TERMINERFASSUNG_KUERZEL;

export type PxConnectTerminField = {
    name: string;
    label: string;
    type: 'date' | 'text';
    /** Feld steht alleine in seiner Zeile (halbe Breite + leere Füllzelle daneben). */
    standalone?: boolean;
};

/**
 * Volle Replik des externen PX-Connect-Terminerfassungsformulars: ALLE Termintypen als fixe,
 * beschriftete Felder — inkl. der im Portal bereits als `Termin`-Entity existierenden (bewusste
 * Dopplung, weil reine Anzeige-Replik ohne Verbindung zu den echten `Termin`-Entities).
 * Nur Datum (keine Uhrzeit), alles optional. Kommentare als einfache Textfelder.
 *
 * Layout: Erstkontakt und Schaden abgeschlossen stehen alleine (volle Zeile), dazwischen bilden
 * die zusammengehörigen Termine Paare (Leckortung, Trocknung, Wiederherstellung). Die weiteren
 * Ortstermine (Datum + Kommentar) folgen als dynamisches Field-Array, siehe PxConnectTerminBlock.
 */
export const pxConnectTerminFields: PxConnectTerminField[] = [
    { name: 'erstkontakt', label: 'Erstkontakt', type: 'date', standalone: true },
    { name: 'erstterminLeckortung', label: 'Ersttermin / Leckortungstermin', type: 'date' },
    { name: 'eingangLeckortungsbericht', label: 'Eingang Leckortungsbericht', type: 'date' },
    { name: 'aufbauTrocknung', label: 'Aufbau Trocknung', type: 'date' },
    { name: 'abbauTrocknung', label: 'Abbau Trocknung', type: 'date' },
    { name: 'startWiederherstellung', label: 'Start Wiederherstellung', type: 'date' },
    { name: 'endeWiederherstellung', label: 'Ende Wiederherstellung', type: 'date' },
    { name: 'schadenAbgeschlossen', label: 'Schaden abgeschlossen', type: 'date', standalone: true },
];

/**
 * Reihenfolge-Regeln zwischen Datumsfeldern: `end` darf nicht vor `start` liegen.
 * Datumswerte stammen aus <input type="date"> (Format „YYYY-MM-DD"), daher genügt
 * der lexikografische Vergleich.
 */
export type PxConnectDateConstraint = {
    start: string;
    end: string;
    message: string;
};

export const pxConnectDateConstraints: PxConnectDateConstraint[] = [
    {
        start: 'aufbauTrocknung',
        end: 'abbauTrocknung',
        message: 'Abbau Trocknung darf nicht vor Aufbau Trocknung liegen.',
    },
    {
        start: 'startWiederherstellung',
        end: 'endeWiederherstellung',
        message: 'Ende Wiederherstellung darf nicht vor Start Wiederherstellung liegen.',
    },
];

/** Liefert Fehlermeldungen, gekeyt nach dem jeweils verletzenden `end`-Feldnamen. */
export const validatePxConnectTermine = (
    values: Record<string, string>
): Record<string, string> => {
    const errors: Record<string, string> = {};
    for (const { start, end, message } of pxConnectDateConstraints) {
        const startValue = values[start];
        const endValue = values[end];
        if (startValue && endValue && endValue < startValue) {
            errors[end] = message;
        }
    }
    return errors;
};

/** Eine Zeile des „Weitere Ortstermine"-Field-Arrays. */
export type PxConnectOrtstermin = {
    datum: string;
    kommentar: string;
};

export const emptyPxConnectOrtstermin: PxConnectOrtstermin = { datum: '', kommentar: '' };
